export interface RgbValues {
  red: number;
  green: number;
  blue: number;
}

export interface RgbSignalFrame {
  time: number;
  forehead: RgbValues;
  leftCheek: RgbValues;
  rightCheek: RgbValues;
  nose: RgbValues;
  combined: RgbValues;
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const SAMPLE_STEP = 2;
const DEFAULT_FPS = 30;

export interface RgbCollectorOptions {
  videoElement: HTMLVideoElement;
  fps?: number;
}

export class RgbCollector {
  private videoElement: HTMLVideoElement;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private isCollecting = false;
  private frames: RgbSignalFrame[] = [];
  private collectionStartTime = 0;
  private frameTimerId: number | null = null;
  private fps: number;

  constructor(options: RgbCollectorOptions) {
    this.videoElement = options.videoElement;
    this.fps = options.fps ?? DEFAULT_FPS;
  }

  startCollection(): void {
    if (this.isCollecting) {
      console.warn("RGB collection: Already collecting");
      return;
    }
    this.frames = [];
    this.collectionStartTime = Date.now();
    this.isCollecting = true;
    this.ensureCanvas();
    const deferMs = 150;
    this.frameTimerId = window.setTimeout(() => {
      this.frameTimerId = null;
      this.scheduleFrame();
    }, deferMs);
    console.info("RGB collection: Started");
  }

  stopCollection(): string | null {
    if (!this.isCollecting) {
      return null;
    }
    this.isCollecting = false;
    if (this.frameTimerId != null) {
      clearTimeout(this.frameTimerId);
      this.frameTimerId = null;
    }
    if (this.frames.length === 0) {
      console.warn("RGB collection: No frames collected");
      return null;
    }
    const json = this.buildJson();
    console.info(`RGB collection: Stopped, ${this.frames.length} frames`);
    return json;
  }

  getCollectionState(): boolean {
    return this.isCollecting;
  }

  private ensureCanvas(): void {
    if (this.canvas && this.ctx) return;
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d");
    if (!this.ctx) {
      throw new Error("RGB collection: Failed to get canvas 2d context");
    }
  }

  private scheduleFrame(): void {
    if (!this.isCollecting) return;
    const interval = 1000 / this.fps;
    const elapsed = Date.now() - this.collectionStartTime;
    const nextTick = Math.max(0, interval - (elapsed % interval));
    this.frameTimerId = window.setTimeout(() => {
      this.frameTimerId = null;
      this.captureFrame();
      this.scheduleFrame();
    }, nextTick);
  }

  private captureFrame(): void {
    if (!this.ctx || !this.canvas || !this.videoElement) return;
    if (this.videoElement.readyState < 1) {
      this.scheduleFrame();
      return;
    }
    const w = this.videoElement.videoWidth || 0;
    const h = this.videoElement.videoHeight || 0;
    if (w <= 0 || h <= 0) {
      this.scheduleFrame();
      return;
    }
    this.canvas.width = w;
    this.canvas.height = h;
    this.ctx.drawImage(this.videoElement, 0, 0, w, h);
    const imageData = this.ctx.getImageData(0, 0, w, h);
    const faceRoi = this.getDefaultFaceRoi(w, h);
    const time = (Date.now() - this.collectionStartTime) / 1000;
    const forehead = this.calculateForeheadRoi(faceRoi);
    const leftCheek = this.calculateLeftCheekRoi(faceRoi);
    const rightCheek = this.calculateRightCheekRoi(faceRoi);
    const nose = this.calculateNoseRoi(faceRoi);
    const foreheadRgb = this.extractRgbFromRegion(imageData, forehead, w, h);
    const leftCheekRgb = this.extractRgbFromRegion(imageData, leftCheek, w, h);
    const rightCheekRgb = this.extractRgbFromRegion(
      imageData,
      rightCheek,
      w,
      h
    );
    const noseRgb = this.extractRgbFromRegion(imageData, nose, w, h);
    const combined: RgbValues = {
      red:
        (foreheadRgb.red + leftCheekRgb.red + rightCheekRgb.red + noseRgb.red) /
        4,
      green:
        (foreheadRgb.green +
          leftCheekRgb.green +
          rightCheekRgb.green +
          noseRgb.green) /
        4,
      blue:
        (foreheadRgb.blue +
          leftCheekRgb.blue +
          rightCheekRgb.blue +
          noseRgb.blue) /
        4,
    };
    this.frames.push({
      time,
      forehead: foreheadRgb,
      leftCheek: leftCheekRgb,
      rightCheek: rightCheekRgb,
      nose: noseRgb,
      combined,
    });
  }

  private getDefaultFaceRoi(width: number, height: number): Rect {
    const w = width * 0.6;
    const h = height * 0.8;
    return {
      left: (width - w) / 2,
      top: (height - h) / 2,
      width: w,
      height: h,
    };
  }

  private calculateForeheadRoi(face: Rect): Rect {
    return {
      left: face.left + face.width * 0.1,
      top: face.top + face.height * 0.05,
      width: face.width * 0.8,
      height: face.height * 0.25,
    };
  }

  private calculateLeftCheekRoi(face: Rect): Rect {
    return {
      left: face.left + face.width * 0.05,
      top: face.top + face.height * 0.4,
      width: face.width * 0.3,
      height: face.height * 0.3,
    };
  }

  private calculateRightCheekRoi(face: Rect): Rect {
    return {
      left: face.left + face.width * 0.65,
      top: face.top + face.height * 0.4,
      width: face.width * 0.3,
      height: face.height * 0.3,
    };
  }

  private calculateNoseRoi(face: Rect): Rect {
    return {
      left: face.left + face.width * 0.375,
      top: face.top + face.height * 0.35,
      width: face.width * 0.25,
      height: face.height * 0.3,
    };
  }

  private extractRgbFromRegion(
    imageData: ImageData,
    region: Rect,
    imageWidth: number,
    imageHeight: number
  ): RgbValues {
    const left = Math.max(0, Math.min(Math.floor(region.left), imageWidth - 1));
    const top = Math.max(0, Math.min(Math.floor(region.top), imageHeight - 1));
    const right = Math.max(
      left + 1,
      Math.min(Math.ceil(region.left + region.width), imageWidth)
    );
    const bottom = Math.max(
      top + 1,
      Math.min(Math.ceil(region.top + region.height), imageHeight)
    );
    let totalR = 0,
      totalG = 0,
      totalB = 0;
    let count = 0;
    const data = imageData.data;
    for (let y = top; y < bottom; y += SAMPLE_STEP) {
      for (let x = left; x < right; x += SAMPLE_STEP) {
        const i = (y * imageWidth + x) * 4;
        totalR += data[i];
        totalG += data[i + 1];
        totalB += data[i + 2];
        count++;
      }
    }
    if (count === 0) return { red: 0, green: 0, blue: 0 };
    return {
      red: totalR / count,
      green: totalG / count,
      blue: totalB / count,
    };
  }

  private buildJson(): string {
    const time: number[] = [];
    const forehead = {
      red: [] as number[],
      green: [] as number[],
      blue: [] as number[],
    };
    const leftCheek = {
      red: [] as number[],
      green: [] as number[],
      blue: [] as number[],
    };
    const rightCheek = {
      red: [] as number[],
      green: [] as number[],
      blue: [] as number[],
    };
    const nose = {
      red: [] as number[],
      green: [] as number[],
      blue: [] as number[],
    };
    const combined = {
      red: [] as number[],
      green: [] as number[],
      blue: [] as number[],
    };
    for (const frame of this.frames) {
      time.push(frame.time);
      forehead.red.push(frame.forehead.red);
      forehead.green.push(frame.forehead.green);
      forehead.blue.push(frame.forehead.blue);
      leftCheek.red.push(frame.leftCheek.red);
      leftCheek.green.push(frame.leftCheek.green);
      leftCheek.blue.push(frame.leftCheek.blue);
      rightCheek.red.push(frame.rightCheek.red);
      rightCheek.green.push(frame.rightCheek.green);
      rightCheek.blue.push(frame.rightCheek.blue);
      nose.red.push(frame.nose.red);
      nose.green.push(frame.nose.green);
      nose.blue.push(frame.nose.blue);
      combined.red.push(frame.combined.red);
      combined.green.push(frame.combined.green);
      combined.blue.push(frame.combined.blue);
    }
    const result = {
      result: {
        time,
        multiROI: {
          forehead,
          leftCheek,
          rightCheek,
          nose,
          combined,
        },
      },
    };
    return JSON.stringify(result);
  }
}
